
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Info, AlertCircle, Clock, Loader2 } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { TariffCardsGrid } from "./TariffCardsGrid";
import { TariffSuccess } from "./TariffSuccess";
import { TariffActionButton } from "./TariffActionButton";
import { tariffs } from "./tariff.constants";
import { useTariffSelection } from "./useTariffSelection";
import { generateDealNumber } from "@/utils/dateUtils";
import { addNotification } from '@/hooks/useNotifications';

interface TariffSelectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  amoCustomerCode?: string | null;
  amoDealCode?: string | null;
  clientNumber?: string | null;
}

export const TariffSelectionDialog: React.FC<TariffSelectionDialogProps> = ({
  open,
  onOpenChange,
  amoCustomerCode,
  amoDealCode,
  clientNumber,
}) => {
  const [sentTariff, setSentTariff] = useState<null | { title: string; price: number }>(null);
  const [showCheck, setShowCheck] = useState(false);
  const [dealNumber, setDealNumber] = useState<string | null>(null);
  const isMobile = useIsMobile();

  const { 
    selected, 
    setSelected, 
    sendTariff, 
    loading, 
    responseStatus,
    retryCount, 
    retryRequest 
  } = useTariffSelection({
    amoCustomerCode, amoDealCode, clientNumber,
    onSuccess: (tariff) => {
      setSentTariff(tariff);
      setDealNumber(generateDealNumber());
      setShowCheck(true);
      
      // Добавляем уведомление об успешном выборе тарифа в центральную систему уведомлений
      addNotification({
        title: 'Тариф выбран', 
        message: `Вы успешно выбрали тариф "${tariff.title}" за ${tariff.price} руб.`,
        type: 'system'
      });
      
      setTimeout(() => {
        setShowCheck(false);
        onOpenChange(false);
        setSentTariff(null);
        setSelected(null);
        setDealNumber(null);
      }, 2200);
    },
  });

  // Эффект для отображения уведомлений о повторных попытках
  useEffect(() => {
    if (retryCount > 1) {
      addNotification({
        title: `Повторная попытка #${retryCount}`,
        message: "Ожидаем ответа от сервера...",
        type: 'warning'
      });
      
      // Добавляем дополнительное уведомление для повторных попыток
      if (retryCount >= 2) {
        addNotification({
          title: `Повторная попытка #${retryCount}`,
          message: "Возникли проблемы с соединением при выборе тарифа. Повторная отправка запроса.",
          type: 'warning'
        });
      }
    }
  }, [retryCount]);

  // Определяем текст кнопки в зависимости от статуса запроса
  const getButtonText = () => {
    if (loading) {
      if (responseStatus === 'sending') return 'Отправка запроса...';
      return 'Обработка...';
    }
    
    if (responseStatus === 'timeout') return 'Повторить запрос';
    if (responseStatus === 'error') return 'Попробовать снова';
    
    return 'Выбрать тариф';
  };

  // Добавляем иконку в зависимости от статуса запроса
  const getStatusIcon = () => {
    if (loading) return <Loader2 className="h-5 w-5 animate-spin" />;
    if (responseStatus === 'timeout') return <Clock className="h-5 w-5" />;
    if (responseStatus === 'error') return <AlertCircle className="h-5 w-5" />;
    return null;
  };

  return (
    <>
      {!showCheck ? (
        <div className="flex flex-col">
          <DialogHeader className="mb-6">
            <DialogTitle className="text-white text-xl md:text-3xl font-bold text-center mb-2 drop-shadow-lg select-none">
              Выбор тарифа аренды
            </DialogTitle>
            <DialogDescription className="text-gray-100 text-base sm:text-lg text-center select-none mb-2">
              Подберите оптимальный тариф для вашей работы
            </DialogDescription>
          </DialogHeader>

          <TariffCardsGrid
            selected={selected}
            setSelected={setSelected}
            isMobile={isMobile}
          />
          <div className="sticky bottom-0 bg-gradient-to-t from-slate-900 pt-6 pb-2 px-2">
            <div className="flex flex-col gap-2">
              <TariffActionButton
                disabled={loading || !selected}
                loading={loading}
                icon={getStatusIcon()}
                text={getButtonText()}
                onClick={() => {
                  if (selected) {
                    const selectedTariff = tariffs.find((t) => t.key === selected)!;
                    if (responseStatus === 'timeout' || responseStatus === 'error') {
                      retryRequest(selectedTariff);
                    } else {
                      sendTariff(selectedTariff);
                    }
                  }
                }}
              />
              
              {/* Информационный блок с подсказкой */}
              {responseStatus === 'timeout' && (
                <div className="mt-2 p-2 bg-yellow-500/20 border border-yellow-400/40 rounded-md">
                  <p className="text-sm text-yellow-300 flex items-center gap-1">
                    <Clock size={16} />
                    Превышено время ожидания ответа от сервера
                  </p>
                </div>
              )}
              
              {responseStatus === 'error' && (
                <div className="mt-2 p-2 bg-red-500/20 border border-red-400/40 rounded-md">
                  <p className="text-sm text-red-300 flex items-center gap-1">
                    <AlertCircle size={16} />
                    Произошла ошибка при обработке запроса
                  </p>
                </div>
              )}
            </div>
            
            <div className="mt-4 text-center flex justify-center items-center gap-2 text-gray-200 text-xs select-none">
              <Info size={18} className="text-gray-300" />
              При выборе тарифа учитывайте ваши рабочие задачи
            </div>
          </div>
        </div>
      ) : (
        <TariffSuccess
          showCheck={showCheck}
          sentTariff={sentTariff}
          clientNumber={clientNumber}
          dealNumber={dealNumber || undefined}
        />
      )}
    </>
  );
};
